import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { Magazine, MagazineProcedureResult, MagazineSaveRequest } from '../models/magazine.model';

@Injectable({ providedIn: 'root' })
export class MagazineService {
  readonly magazines = signal<Magazine[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');

  constructor(private readonly api: ApiService) {}

  loadMagazines(): void {
    this.loading.set(true);
    this.error.set('');
    this.api
      .get<unknown[]>('/Magazines')
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) =>
          this.magazines.set(Array.isArray(response) ? response.map((row) => this.normalize(row)) : []),
        error: () => this.error.set('Could not load magazines. Check the API server.')
      });
  }

  getById(id: number): Magazine | undefined {
    return this.magazines().find((magazine) => magazine.MagId === id);
  }

  loadMagazineById(id: number): Observable<Magazine | null> {
    return this.api.get<unknown[] | unknown>(`/Magazines?magId=${id}`).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response[0] ? this.normalize(response[0]) : null;
        }
        return response ? this.normalize(response) : null;
      })
    );
  }

  saveMagazine(payload: MagazineSaveRequest): Observable<MagazineProcedureResult> {
    return this.api.post<MagazineProcedureResult>('/Magazines', payload);
  }

  importMagazinePdf(formData: FormData): Observable<MagazineProcedureResult> {
    return this.api.postForm<MagazineProcedureResult>('/Magazines/import-pdf', formData);
  }

  deleteMagazine(magId: number): Observable<MagazineProcedureResult> {
    return this.api.delete<MagazineProcedureResult>(`/Magazines/${magId}`);
  }

  private normalize(source: any): Magazine {
    const publishDate = source.MagPublishDate ?? source.magPublishDate;
    return {
      MagId: Number(source.MagId ?? source.magId ?? 0),
      MagTitle: source.MagTitle ?? source.magTitle ?? '',
      MagGenre: source.MagGenre ?? source.magGenre ?? '',
      MagIssueNumber: source.MagIssueNumber ?? source.magIssueNumber ?? '',
      MagPublishDate: publishDate ? String(publishDate) : '',
      MagUrl: source.MagUrl ?? source.magUrl ?? '',
      MagDescription: source.MagDescription ?? source.magDescription ?? '',
      MagDoc: source.MagDoc ?? source.magDoc ?? ''
    };
  }
}
